import React, { useEffect, useState } from "react";
import { ArrowUp, ArrowDown, Loader2, Save } from "lucide-react";
import { toast } from "sonner";
import { Switch } from "../../components/ui/switch";
import { api } from "../../lib/api";
import { RangeSwitch, TotalsBar, RowStats } from "./VideoStats";

const Row = ({ v, i, last, set, move, stats }) => (
  <div className={`flex items-center gap-4 rounded-2xl border bg-white p-3 ${v.show ? "border-[#bfe7de]" : "border-[#e3f0ed] opacity-70"}`} data-testid={`admin-video-${v.n}`}>
    <a href={v.src} target="_blank" rel="noreferrer"><img src={v.poster} alt="" className="h-24 w-[54px] shrink-0 rounded-lg object-cover" /></a>
    <div className="min-w-0 flex-1">
      <div className="text-[12px] font-semibold text-[#6b7c79]">Post {v.n}</div>
      <input value={v.title} onChange={(e) => set({ ...v, title: e.target.value })} className="mt-1 w-full rounded-lg border border-[#dcebe8] px-3 py-2 text-[14.5px] outline-none focus:border-[#0e8c7f]" data-testid={`admin-video-title-${v.n}`} />
      <RowStats n={v.n} s={stats} />
    </div>
    <label className="flex flex-col items-center gap-1 text-[12px] text-[#5b6d6a]">
      <Switch checked={v.show} onCheckedChange={(s) => set({ ...v, show: s })} data-testid={`admin-video-show-${v.n}`} />
      {v.show ? "Shown" : "Hidden"}
    </label>
    <div className="flex flex-col gap-1">
      <button disabled={i === 0} onClick={() => move(i, -1)} className="rounded-md border border-[#dcebe8] p-1.5 disabled:opacity-30" aria-label="Move up" data-testid={`admin-video-up-${v.n}`}><ArrowUp size={14} /></button>
      <button disabled={last} onClick={() => move(i, 1)} className="rounded-md border border-[#dcebe8] p-1.5 disabled:opacity-30" aria-label="Move down" data-testid={`admin-video-down-${v.n}`}><ArrowDown size={14} /></button>
    </div>
  </div>
);

const VideosTab = () => {
  const [items, setItems] = useState(null);
  const [busy, setBusy] = useState(false);
  const [days, setDays] = useState(30);
  const [stats, setStats] = useState(null);
  useEffect(() => { api.get("/admin/videos").then((r) => setItems(r.data)); }, []);
  useEffect(() => { api.get(`/admin/videos/stats?days=${days}`).then((r) => setStats(r.data)).catch(() => {}); }, [days]);
  if (!items) return <Loader2 className="animate-spin text-[#0e8c7f]" />;
  const set = (i) => (v) => setItems(items.map((x, j) => (j === i ? v : x)));
  const move = (i, d) => { const a = [...items]; [a[i], a[i + d]] = [a[i + d], a[i]]; setItems(a); };
  const save = async () => {
    setBusy(true);
    try { const { data } = await api.put("/admin/videos", { items: items.map(({ n, title, show }) => ({ n, title, show })) }); setItems(data); toast.success("Videos saved — website updated"); }
    catch (e) { toast.error(e?.response?.data?.detail || "Failed to save"); }
    finally { setBusy(false); }
  };
  return (
    <div className="max-w-5xl space-y-4" data-testid="admin-videos-tab">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h3 className="text-[18px] font-bold text-[#0f2e2a]">Video stats</h3>
        <RangeSwitch days={days} setDays={setDays} />
      </div>
      <TotalsBar totals={stats?.totals} />
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="text-[14px] text-[#5b6d6a]">Choose which videos appear in the <b>Watch &amp; Learn</b> section on your home page, edit titles, and set the order. <b>{items.filter((v) => v.show).length}</b> shown.</p>
        <button onClick={save} disabled={busy} className="sw-btn-primary !py-2.5 disabled:opacity-60" data-testid="admin-videos-save">{busy ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}Save</button>
      </div>
      {items.map((v, i) => <Row key={v.n} v={v} i={i} last={i === items.length - 1} set={set(i)} move={move} stats={stats?.videos?.[v.n]} />)}
    </div>
  );
};

export default VideosTab;
